import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  daysRemainingTone,
  formatDaysRemaining,
  formatShortDate,
  isActiveStatus,
  scorePercent,
} from "../AuditHub/utils";
import AuditAccountScores from "./AuditAccountScores";
import AuditReportButton from "./AuditReportButton";
import {
  CardHeader,
  CardShell,
  FAILED_COLOR,
  FAILED_TEXT_COLOR,
  formatCount,
  labelSx,
  PASSED_COLOR,
  PASSED_TEXT_COLOR,
  Tile,
  tileGridSx,
  wideTileSx,
} from "./AuditCardParts";
import { ComplianceScoreCard } from "./AuditAccountCard";
import { AH_BORDER, AH_MUTED, AH_TEXT, MetaSeparator } from "../AuditHub/theme";
import { frameworkMatches } from "./ControlAuditPanel";
import { getComplianceControlSections } from "../../../helpers/auth_aws_helper";
import {
  addComponents,
  assessedFromComponents,
  baselineFromComponents,
  EMPTY_COMPONENTS,
  readComponents,
} from "./auditMetrics";

const DUE_COLOR = { overdue: "#dc2626", warn: "#d97706", default: "inherit" };

const sumOf = (scopes, read, add, empty, field) =>
  scopes.reduce((total, scope) => add(total, read(scope[field])), empty);

/**
 * Audit-level score when this framework is the whole assessment; otherwise the same
 * passed / (passed + failed) ratio the API uses, taken from these scopes only.
 */
const scoreOf = (wholeAudit, apiValue, computed) => (wholeAudit ? scorePercent(apiValue) : computed);

const PENDING_SCAN = "Pending automated scan completion";

/** One summary per scan run. The account on screen is already loaded by the page. */
const resourceCache = new Map();

const readResourceSummary = (body) => ({
  total: Number(body?.totalResources) || 0,
  passed: Number(body?.passedResources) || 0,
  failed: Number(body?.failedResources) || 0,
});

const loadResourceSummary = (compliance, runId) => {
  const key = `${compliance}\n${runId}`;
  const cached = resourceCache.get(key);
  if (cached) return cached;
  const pending = getComplianceControlSections({ compliance, runId })
    .then((body) => {
      if (!body || body.status !== 200) throw new Error("Resource summary unavailable");
      return readResourceSummary(body);
    })
    .catch((error) => {
      resourceCache.delete(key);
      throw error;
    });
  resourceCache.set(key, pending);
  return pending;
};

const treeValueSx = {
  fontWeight: 700,
  color: AH_TEXT,
  lineHeight: 1.15,
  fontVariantNumeric: "tabular-nums",
};

const TreeCount = ({ label, value, color, size = 16 }) => (
  <Box sx={{ minWidth: 0, textAlign: "center" }}>
    <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color, lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Typography noWrap sx={{ ...treeValueSx, fontSize: size }}>
      {formatCount(value)}
    </Typography>
  </Box>
);

/** Total sits above Passed and Failed. */
const AutomatedTree = ({ total, passed, failed }) => (
  <Box sx={{ width: "100%", maxWidth: 240, mx: "auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.25 }}>
    <TreeCount label="Total" value={total} color={AH_MUTED} size={24} />
    <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", columnGap: 2 }}>
      <TreeCount label="Passed" value={passed} color={PASSED_TEXT_COLOR} />
      <TreeCount label="Failed" value={failed} color={FAILED_TEXT_COLOR} />
    </Box>
  </Box>
);

const ResourcesScanned = ({ summary }) => {
  if (!summary || summary.status === "empty") {
    return <Typography sx={{ fontSize: 13, color: AH_MUTED }}>{PENDING_SCAN}</Typography>;
  }
  if (summary.status !== "ready") {
    return <Typography sx={{ fontSize: 13, color: AH_MUTED }}>—</Typography>;
  }
  const passedPercent = summary.total > 0 ? (summary.passed / summary.total) * 100 : 0;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={labelSx}>Resource Scanned (Automated): {formatCount(summary.total)}</Typography>
      <Box sx={{ height: 7, width: "100%", mt: 0.75, borderRadius: 5, overflow: "hidden", display: "flex", bgcolor: FAILED_COLOR }}>
        <Box sx={{ width: `${passedPercent}%`, bgcolor: PASSED_COLOR }} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 0.75 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: PASSED_TEXT_COLOR, whiteSpace: "nowrap" }}>
          {formatCount(summary.passed)} Passed
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: FAILED_TEXT_COLOR, whiteSpace: "nowrap" }}>
          {formatCount(summary.failed)} Failed
        </Typography>
      </Box>
    </Box>
  );
};

const AuditOverviewHeader = ({ audit, compliance, accountNumber, onAccountChange, activeRunId = "", activeResources = null }) => {
  const accounts = useMemo(() => (Array.isArray(audit?.accounts) ? audit.accounts : []), [audit]);
  const scopes = useMemo(
    () =>
      accounts
        .map((account) => (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework)))
        .filter(Boolean),
    [accounts, compliance]
  );
  const wholeAudit = useMemo(() => {
    const allScopes = accounts.reduce((count, account) => count + (account.scopes || []).length, 0);
    return scopes.length > 0 && scopes.length === allScopes;
  }, [accounts, scopes]);

  const components = useMemo(
    () => sumOf(scopes, readComponents, addComponents, EMPTY_COMPONENTS, "components"),
    [scopes]
  );
  const score = scoreOf(wholeAudit, audit?.auditScore, assessedFromComponents(components));
  const baseline = scoreOf(wholeAudit, audit?.baseScore, baselineFromComponents(components));
  const scannedRunIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    scopes.forEach((scope) => {
      const runId = scope?.latestRunId;
      if (!runId || seen.has(runId)) return;
      seen.add(runId);
      ids.push(runId);
    });
    return ids;
  }, [scopes]);
  const scannedRunKey = scannedRunIds.join("\n");
  const [otherResources, setOtherResources] = useState({});
  useEffect(() => {
    const ids = scannedRunKey ? scannedRunKey.split("\n") : [];
    const missing = ids.filter((runId) => runId !== activeRunId);
    if (!compliance || !missing.length) return undefined;
    let cancelled = false;
    missing.forEach((runId) => {
      loadResourceSummary(compliance, runId)
        .then((summary) => {
          if (!cancelled) setOtherResources((prev) => (prev[runId] ? prev : { ...prev, [runId]: summary }));
        })
        .catch(() => {
          if (!cancelled) setOtherResources((prev) => (runId in prev ? prev : { ...prev, [runId]: null }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [compliance, scannedRunKey, activeRunId]);
  const resourceSummary = useMemo(() => {
    const ids = scannedRunKey ? scannedRunKey.split("\n") : [];
    if (!ids.length) return { status: "empty" };
    let total = 0;
    let passed = 0;
    let failed = 0;
    let pending = false;
    ids.forEach((runId) => {
      const row = runId === activeRunId ? activeResources : otherResources[runId];
      if (row === undefined || (runId === activeRunId && activeResources == null)) {
        pending = true;
        return;
      }
      if (!row) return;
      total += row.total;
      passed += row.passed;
      failed += row.failed;
    });
    if (pending) return { status: "loading" };
    return { status: "ready", total, passed, failed };
  }, [scannedRunKey, activeRunId, activeResources, otherResources]);
  const scannedCount = scopes.filter((scope) => scope.latestRunId).length;
  const scoredCount = scopes.filter((scope) => scope.hasScanResult).length;
  const dueTone = daysRemainingTone(audit);
  const dueRemaining = isActiveStatus(audit?.status) && audit?.dueDate ? formatDaysRemaining(audit) : "";

  return (
    <CardShell>
      <CardHeader
        eyebrow="Assessment Overview"
        compliance={compliance}
        meta={
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5 }}>
            <span>{`All ${accounts.length} accounts`}</span>
            <MetaSeparator />
            <span>{`${scannedCount} of ${accounts.length} scanned for this framework`}</span>
            {audit?.dueDate ? (
              <>
                <MetaSeparator />
                <span>{`Due Date ${formatShortDate(audit.dueDate)}`}</span>
                {dueRemaining && dueRemaining !== "—" ? (
                  <>
                    <MetaSeparator />
                    <span>
                      Days Remaining{" "}
                      <Box component="span" sx={{ color: DUE_COLOR[dueTone] || DUE_COLOR.default, fontWeight: 600 }}>
                        {dueRemaining}
                      </Box>
                    </span>
                  </>
                ) : null}
              </>
            ) : null}
          </Box>
        }
        action={<AuditReportButton auditId={audit?.auditId} auditCode={audit?.auditCode} status={audit?.status} />}
      />
      <Box sx={tileGridSx}>
        <ComplianceScoreCard
          score={score}
          baseline={baseline}
          passed={components.autoPassed + components.manualPassed}
          failed={components.autoFailed + components.manualFailed}
          known
          pendingScan={scoredCount === 0}
        />
        <Tile label="Automated Controls" align="start" sx={{ height: "100%", minHeight: 0 }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
            <AutomatedTree
              total={components.autoPassed + components.autoFailed}
              passed={components.autoPassed}
              failed={components.autoFailed}
            />
            <Box sx={{ mt: "auto" }}>
              <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${AH_BORDER}` }}>
                <ResourcesScanned summary={resourceSummary} />
              </Box>
            </Box>
          </Box>
        </Tile>
        <AuditAccountScores
          accounts={accounts}
          compliance={compliance}
          accountNumber={accountNumber}
          onAccountChange={onAccountChange}
          manual={{
            total: components.manualPassed + components.manualFailed + components.notApplicable + components.undecided,
            passed: components.manualPassed,
            failed: components.manualFailed,
          }}
          sx={wideTileSx}
        />
      </Box>
    </CardShell>
  );
};

export default AuditOverviewHeader;
